import React, { createContext, useContext, useState, useEffect } from 'react';
import { getMe } from '../api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const savedUser = sessionStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        if (parsed && parsed.id) {
          setUser(parsed);
          getMe()
            .then((res) => {
              setUser(res.data);
              sessionStorage.setItem('user', JSON.stringify(res.data));
            })
            .catch(() => {
              sessionStorage.removeItem('token');
              sessionStorage.removeItem('user');
              setUser(null);
            })
            .finally(() => setLoading(false));
          return;
        }
      } catch (e) {
        // Corrupted sessionStorage data - clear and restart
      }
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
    }
    setLoading(false);
  }, []);

  const loginUser = (token, userData) => {
    if (!token || !userData || !userData.id) {
      return;
    }
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setUser(null);
  };

  const value = {
    user,
    loading,
    loginUser,
    logout,
    isManager: user?.role === 'manager',
    isLead: user?.role_level === 'lead',
    canCreateAccounts: ['l1_tech', 'l2_tech', 'l3_tech', 'manager'].includes(user?.role),
    canEditAccounts: ['l1_tech', 'l2_tech', 'l3_tech', 'manager'].includes(user?.role),
    canAddDevices: ['l1_tech', 'l2_tech', 'l3_tech', 'manager'].includes(user?.role),
    canAddBlockers: ['l1_tech', 'l2_tech', 'l3_tech', 'manager'].includes(user?.role),
    canResolveTech: ['l1_tech', 'l2_tech', 'l3_tech', 'manager'].includes(user?.role),
    canNotifyClient: ['inbound_sales', 'manager'].includes(user?.role),
    canVerifyL3: ['l3_tech', 'manager'].includes(user?.role),
    canApprove: user?.role === 'manager',
    canMarkCompleted: ['l3_tech', 'manager'].includes(user?.role),
    canViewDashboard: ['manager', 'inbound_sales'].includes(user?.role),
    canViewReports: user?.role_level === 'lead' || user?.role === 'manager',
    canManageUsers: user?.role === 'manager',
    canReassign: user?.role_level === 'lead' || user?.role === 'manager',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
